import React, { useEffect, useMemo, useState } from 'react';
import {
  Activity,
  ArrowLeft,
  Bot,
  Download,
  FileText,
  Globe,
  HardDrive,
  Monitor,
  Usb,
} from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import EmptyState from '../components/EmptyState';
import LoadingSkeleton from '../components/LoadingSkeleton';
import StatusBadge from '../components/StatusBadge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { devicesApi } from '../lib/api';

const EVENT_OPTIONS = [
  { value: 'all', label: 'All events' },
  { value: 'activity', label: 'Activity' },
  { value: 'web', label: 'Web' },
  { value: 'file', label: 'File' },
  { value: 'usb', label: 'USB' },
  { value: 'install', label: 'Install' },
  { value: 'ai_tool', label: 'AI' },
];

function formatTimestamp(value) {
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).format(new Date(value));
}

function durationBetween(start, end) {
  if (!start || !end) return '—';
  const seconds = Math.max(0, Math.round((new Date(end) - new Date(start)) / 1000));
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  if (hours) return `${hours}h ${minutes % 60}m`;
  return `${minutes}m`;
}

function eventMeta(event) {
  switch (event.event_type) {
    case 'activity':
      return {
        icon: Activity,
        ring: 'border-blue-500/40 bg-blue-500/10 text-blue-400',
        title: event.app_name || 'Activity',
        detail: event.window_title || event.process_name || 'Window focus changed',
      };
    case 'web':
      return {
        icon: Globe,
        ring: 'border-purple-500/40 bg-purple-500/10 text-purple-400',
        title: event.domain || 'Web event',
        detail: event.browser || 'Browser activity',
      };
    case 'file':
      return {
        icon: FileText,
        ring: 'border-amber-500/40 bg-amber-500/10 text-amber-400',
        title: event.action || 'File event',
        detail: event.path || 'File operation',
      };
    case 'usb':
      return {
        icon: Usb,
        ring: 'border-rose-500/40 bg-rose-500/10 text-rose-400',
        title: event.action || 'USB event',
        detail: event.label || event.usb_id || 'USB activity',
      };
    case 'install':
      return {
        icon: Download,
        ring: 'border-emerald-500/40 bg-emerald-500/10 text-emerald-400',
        title: event.app_name || 'Install event',
        detail: event.action || 'Application install event',
      };
    case 'ai_tool':
      return {
        icon: Bot,
        ring: 'border-violet-500/40 bg-violet-500/10 text-violet-400',
        title: event.tool_name || 'AI event',
        detail: event.context || 'AI tool interaction',
      };
    default:
      return {
        icon: HardDrive,
        ring: 'border-slate-600 bg-slate-700/30 text-slate-300',
        title: event.event_type || 'Event',
        detail: 'Device event',
      };
  }
}

export default function DeviceTimeline() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [device, setDevice] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [eventType, setEventType] = useState('all');
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const pageSize = 20;

  const loadTimeline = async () => {
    setLoading(true);
    setError('');
    try {
      const [deviceResponse, eventsResponse] = await Promise.all([
        devicesApi.get(id),
        devicesApi.events(id, {
          date,
          event_type: eventType === 'all' ? undefined : eventType,
          page,
          page_size: pageSize,
        }),
      ]);
      setDevice(deviceResponse.data);
      setEvents(eventsResponse.data?.items || []);
      setTotal(eventsResponse.data?.total || 0);
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to load the device timeline.';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTimeline();
  }, [id, page, date, eventType]);

  useEffect(() => {
    setPage(1);
  }, [date, eventType]);

  const filteredEvents = useMemo(() => events, [events]);

  return (
    <div className="min-h-screen space-y-6 bg-slate-900 p-6 text-slate-300" data-testid="device-timeline-page">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="flex items-start gap-3">
          <Button
            variant="outline"
            className="border-slate-600 bg-slate-800 text-slate-300 hover:bg-slate-700"
            onClick={() => navigate('/devices')}
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </Button>
          <div>
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="text-2xl font-bold text-white">{device?.hostname || 'Device timeline'}</h1>
              {device ? <StatusBadge status={device.status || 'offline'} /> : null}
            </div>
            <p className="mt-2 text-slate-500">
              {device?.employee_name || 'Unassigned'} • {device?.os || 'Unknown OS'} • Agent{' '}
              {device?.agent_version || '—'}
            </p>
          </div>
        </div>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <input
            type="date"
            value={date}
            onChange={(event) => setDate(event.target.value)}
            className="rounded-lg border border-slate-600 bg-slate-700 px-3 py-2 text-white outline-none focus:ring-2 focus:ring-blue-500"
          />
          <Select value={eventType} onValueChange={setEventType}>
            <SelectTrigger className="w-[180px] border-slate-600 bg-slate-700 text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="border-slate-700 bg-slate-800 text-slate-200">
              {EVENT_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {error ? (
        <div className="rounded-lg border border-red-500/20 bg-red-500/10 p-4 text-sm text-red-300">
          {error}
        </div>
      ) : null}

      <Card className="border-slate-700 bg-slate-800 shadow-lg">
        <CardHeader className="pb-0">
          <CardTitle className="text-white">Timeline</CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          {loading ? (
            <LoadingSkeleton type="text" count={8} />
          ) : filteredEvents.length ? (
            <div className="relative pl-8">
              <div className="absolute bottom-0 left-3 top-0 w-px bg-slate-700" />
              <div className="space-y-4">
                {filteredEvents.map((event, index) => {
                  const meta = eventMeta(event);
                  const Icon = meta.icon;
                  const severity =
                    event.action === 'blocked' || event.enforcement === 'blocked'
                      ? 'bg-red-500'
                      : event.event_type === 'usb'
                        ? 'bg-amber-500'
                        : 'bg-emerald-500';

                  return (
                    <div key={event.id || index} className="relative">
                      <span className={`absolute -left-[29px] top-5 h-3 w-3 rounded-full ${severity}`} />
                      <div className="rounded-lg border border-slate-700 bg-slate-800 p-4 shadow-lg">
                        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                          <div className="flex items-start gap-3">
                            <div className={`rounded-lg border p-3 ${meta.ring}`}>
                              <Icon className="h-4 w-4" />
                            </div>
                            <div>
                              <p className="font-semibold text-white">{meta.title}</p>
                              <p className="mt-1 text-sm text-slate-400">{meta.detail}</p>
                              <p className="mt-2 text-xs uppercase tracking-[0.2em] text-slate-500">
                                {event.event_type?.replace('_', ' ')}
                              </p>
                            </div>
                          </div>
                          <div className="text-sm text-slate-500 lg:text-right">
                            <p>{formatTimestamp(event.ts_start)}</p>
                            <p className="mt-1">{durationBetween(event.ts_start, event.ts_end)}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <EmptyState
              icon={<Monitor className="h-8 w-8" />}
              title="No events found"
              description="Try a different date or event type to inspect the device timeline."
            />
          )}
        </CardContent>
      </Card>

      {!loading && total > pageSize ? (
        <div className="flex items-center justify-between rounded-lg border border-slate-700 bg-slate-800 p-4">
          <p className="text-sm text-slate-500">
            Page {page} of {Math.max(1, Math.ceil(total / pageSize))}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              className="border-slate-600 bg-slate-900 text-slate-300 hover:bg-slate-700"
              disabled={page <= 1}
              onClick={() => setPage((current) => current - 1)}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              className="border-slate-600 bg-slate-900 text-slate-300 hover:bg-slate-700"
              disabled={page >= Math.ceil(total / pageSize)}
              onClick={() => setPage((current) => current + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
